import { useState } from 'react';
import { ethers } from 'ethers';
import { getContract, getUSDTContract, checkUSDTAllowance } from '../utils/web3';
import { PACKAGE_PRICES, CONTRACT_ADDRESS } from '../config/contract';
import { notifyActivity, isTelegramLinked } from '../utils/telegram';

// Max approval amount (essentially unlimited)
const MAX_APPROVAL = '115792089237316195423570985008687907853269984665640564039457584007913129639935';

// Activity types
const ACTIVITY = {
  REGISTRATION: 0,
  UPGRADE: 1,
  DIRECT_INCOME: 2,
  LEVEL_INCOME: 3,
  POOL_INCOME: 4,
  CLAIMED: 5,
  RETOPUP: 6,
  ROYALTY_INCOME: 7
};

export const useContract = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Send Telegram notification helper
  const sendNotification = async (type, data, userId) => {
    if (isTelegramLinked()) {
      try {
        await notifyActivity(type, data, userId);
      } catch (err) {
        console.log('Telegram notification error:', err);
      }
    }
  };

  const handleTransaction = async (transactionFunction, successMessage, notificationData = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await transactionFunction();
      
      // Send Telegram notification if data provided
      if (notificationData) {
        await sendNotification(
          notificationData.type, 
          notificationData.data, 
          notificationData.userId
        );
      }
      
      return { success: true, data: result, message: successMessage };
    } catch (err) {
      console.error('Transaction error:', err);
      let errorMessage = 'Transaction failed';
      
      // Parse different error types
      if (err.reason) {
        errorMessage = err.reason;
      } else if (err.message) {
        if (err.message.includes('user rejected')) {
          errorMessage = 'Transaction was rejected by user';
        } else if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient BNB for gas fees';
        } else if (err.message.includes('exceeds allowance')) {
          errorMessage = 'USDT approval required. Please try again.';
        } else {
          errorMessage = err.message.substring(0, 100);
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const ensureAllowance = async (requiredAmount) => {
    const usdtContract = getUSDTContract();
    if (!usdtContract) throw new Error('USDT contract not available');
    
    const signer = usdtContract.signer;
    const address = await signer.getAddress();
    const currentAllowance = await checkUSDTAllowance(address);
    
    console.log('Current allowance:', currentAllowance, 'Required:', requiredAmount);
    
    // Always approve max if allowance is less than required
    if (parseFloat(currentAllowance) < parseFloat(requiredAmount)) {
      console.log('Approving USDT...');
      const approveTx = await usdtContract.approve(
        CONTRACT_ADDRESS, 
        ethers.BigNumber.from(MAX_APPROVAL)
      );
      console.log('Waiting for approval confirmation...');
      await approveTx.wait(1); // Wait for 1 confirmation
      console.log('Approval confirmed');
      
      // Add small delay to ensure blockchain state is updated
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  const register = async (referrerId, userId = null) => {
    const packagePrice = PACKAGE_PRICES[0];
    
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        // Ensure allowance first
        await ensureAllowance(packagePrice);
        
        console.log('Submitting registration...');
        const tx = await contract.register(referrerId);
        console.log('Waiting for registration confirmation...');
        return await tx.wait();
      }, 
      'Registration successful',
      userId ? {
        type: ACTIVITY.REGISTRATION,
        data: { amount: packagePrice, packageId: 1 },
        userId
      } : null
    );
  };

  const upgradePackage = async (packageId, userId = null) => {
    const packagePrice = PACKAGE_PRICES[packageId - 1];
    
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        await ensureAllowance(packagePrice);
        
        const tx = await contract.upgradePackage(packageId);
        return await tx.wait();
      }, 
      'Unit activated successfully',
      userId ? {
        type: ACTIVITY.UPGRADE,
        data: { amount: packagePrice, packageId },
        userId
      } : null
    );
  };

  const retopupPackage = async (packageId, userId = null) => {
    const packagePrice = PACKAGE_PRICES[packageId - 1];
    
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        await ensureAllowance(packagePrice);
        
        const tx = await contract.retopupPackage(packageId);
        return await tx.wait();
      }, 
      'Retopup successful',
      userId ? {
        type: ACTIVITY.RETOPUP,
        data: { amount: packagePrice, packageId },
        userId
      } : null
    );
  };

  const claimIncome = async (amount = 0, userId = null) => {
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        const tx = await contract.claimIncome();
        return await tx.wait();
      }, 
      'Income claimed successfully',
      userId ? {
        type: ACTIVITY.CLAIMED,
        data: { amount },
        userId
      } : null
    );
  };

  const claimAllIncome = async (amount = 0, userId = null) => {
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        const tx = await contract.claimAllIncome();
        return await tx.wait();
      }, 
      'All income claimed successfully',
      userId ? {
        type: ACTIVITY.CLAIMED,
        data: { amount },
        userId
      } : null
    );
  };

  const claimPoolIncome = async (packageId, amount = 0, userId = null) => {
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        const tx = await contract.claimPoolIncome(packageId);
        return await tx.wait();
      }, 
      'Pool income claimed successfully',
      userId ? {
        type: ACTIVITY.POOL_INCOME,
        data: { amount, packageId },
        userId
      } : null
    );
  };

  const claimRoyaltyIncome = async (amount = 0, userId = null) => {
    return handleTransaction(
      async () => {
        const contract = getContract();
        if (!contract) throw new Error('Contract not available');
        
        const tx = await contract.claimRoyaltyIncome();
        return await tx.wait();
      }, 
      'Royalty income claimed successfully',
      userId ? {
        type: ACTIVITY.ROYALTY_INCOME,
        data: { amount },
        userId
      } : null
    );
  };

  return {
    isLoading,
    error,
    register,
    upgradePackage,
    retopupPackage,
    claimIncome,
    claimAllIncome,
    claimPoolIncome,
    claimRoyaltyIncome
  };
};
